import BusyType from 'owa-service/lib/contract/BusyType';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { CalendarEvent } from 'mini-calendar-event-types';
import { ClientItemId, getUserMailboxInfo, MailboxInfo } from 'owa-client-ids';
import { getDisplayDateFromEwsDate, getEwsRequestString, getTimestamp } from 'owa-datetime';
import { getFilteredUserConfiguration } from 'mini-settings/lib/selectors/getFilteredUserConfiguration';

const assign = require('object-assign');

/**
 * Converts a CalendarItem instance to CalendarEvent. This function will be used mostly from service
 * functions to convert the returned CalendarItem service objects to CalendarEvent instances for
 * consumption by the application.
 * @param calendarItem The calendar item (service object) to convert
 * @param mailboxInfo The mailbox info for the calendar event
 * @returns The converted CalendarEvent object
 */
export function calendarEvent(
    calendarItem: CalendarItem,
    inputMailboxInfo?: MailboxInfo,
    associatedMailItemId?: ClientItemId
): CalendarEvent {
    // If no mailboxInfo is provided then get the user mailbox info
    const mailboxInfo = inputMailboxInfo || getUserMailboxInfo();

    // Create empty instance of CalendarEvent so that mobX doesn't freak out
    const emptyCalendarEvent: CalendarEvent = new CalendarEvent();

    // Copy then overwrite with proper conversions
    const createdCalendarEvent: CalendarEvent = assign(emptyCalendarEvent, calendarItem, {
        ItemId: calendarItem.ItemId
            ? { ...calendarItem.ItemId, mailboxInfo: mailboxInfo }
            : calendarItem.ItemId,
        ParentFolderId: calendarItem.ParentFolderId
            ? { ...calendarItem.ParentFolderId, mailboxInfo: mailboxInfo }
            : calendarItem.ParentFolderId,
        Start: getDisplayDateFromEwsDate(calendarItem.Start),
        End: getDisplayDateFromEwsDate(calendarItem.End),
        LastModifiedTime: calendarItem.LastModifiedTime
            ? getDisplayDateFromEwsDate(calendarItem.LastModifiedTime)
            : undefined,
        FreeBusyType: calendarItem.FreeBusyType as BusyType,
        ResponseType: calendarItem.ResponseType as ResponseTypeType,
        AppointmentReplyTime: calendarItem.AppointmentReplyTime
            ? getDisplayDateFromEwsDate(calendarItem.AppointmentReplyTime)
            : undefined,
        DoNotForwardMeeting: calendarItem.DoNotForwardMeeting,
        IsOrganizer:
            calendarItem.IsOrganizer ||
            (calendarItem.Organizer?.Mailbox?.EmailAddress &&
                calendarItem.Organizer?.Mailbox?.EmailAddress ===
                    getFilteredUserConfiguration()?.SessionSettings?.UserEmailAddress),
        ExperienceType: calendarItem.ExperienceType
            ? calendarItem.ExperienceType
            : ('Unspecified' as CalendarItemExperienceType),
        Key: {
            timestamp: getTimestamp(getDisplayDateFromEwsDate(calendarItem.Start)),
            itemId: calendarItem.ItemId?.Id,
        },
        ConflictingMeetings: calendarItem.ConflictingMeetings?.Items?.map(item =>
            calendarEvent(item, mailboxInfo)
        ),
        AdjacentMeetings: calendarItem.AdjacentMeetings?.Items?.map(item =>
            calendarEvent(item, mailboxInfo)
        ),
        AssociatedMailItemId: associatedMailItemId,
    } as Partial<CalendarEvent>);

    return createdCalendarEvent;
}

/**
 * Converts a CalendarEvent instance to CalendarItem. This function for the most part will be called
 * only within functions that deal directly with making service requests. Other code should only use
 * CalendarEvent instances.
 * @param calendarEvent The CalendarEvent object
 * @returns The converted CalendarItem instance
 */
export function toCalendarItem(calendarEvent: CalendarEvent): CalendarItem {
    return {
        __type: 'CalendarItem:#Exchange',
        ...calendarEvent,
        Start: calendarEvent.Start && getEwsRequestString(calendarEvent.Start),
        End: calendarEvent.End && getEwsRequestString(calendarEvent.End),
        LastModifiedTime:
            calendarEvent.LastModifiedTime && getEwsRequestString(calendarEvent.LastModifiedTime),
        AppointmentReplyTime:
            calendarEvent.AppointmentReplyTime &&
            getEwsRequestString(calendarEvent.AppointmentReplyTime),
        FreeBusyType: calendarEvent.FreeBusyType as string,
    } as CalendarItem;
}
